/**
 * Motion (BACKLOG #44): one soft way for things to appear and disappear.
 *
 *   - Dialogs with [data-motion] animate out as well as in. close() is wrapped
 *     so every existing caller (buttons, backdrop clicks, Esc) gets the exit
 *     animation without being rewritten. Opening is CSS (critical.css).
 *   - <details> accordions open and close with height + fade.
 *   - Messages that were [hidden] fade in when they appear.
 *   - Lazy images fade in once loaded instead of popping.
 *
 * Nothing animates on page load (no entrance animations on content), and
 * everything is skipped for visitors who ask for reduced motion.
 */
(() => {
  if (window.BrisatoMotion) return;
  window.BrisatoMotion = true;

  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const OUT_MS = 260;
  const EASE = 'cubic-bezier(0.22, 0.8, 0.24, 1)';

  /* ---------- Dialogs: animated close ---------- */
  const proto = window.HTMLDialogElement && HTMLDialogElement.prototype;
  if (proto && !proto.__motion) {
    proto.__motion = true;
    const nativeClose = proto.close;
    const nativeShow = proto.showModal;
    proto.close = function close(value) {
      if (!this.hasAttribute('data-motion') || !this.open || reduce()) return nativeClose.call(this, value);
      if (this.__closing) return undefined;
      this.classList.add('is-closing');
      this.__closing = setTimeout(() => {
        this.__closing = null;
        this.classList.remove('is-closing');
        nativeClose.call(this, value);
      }, OUT_MS);
      return undefined;
    };
    proto.showModal = function showModal() {
      // Reopened while it was still sliding out: just stay open.
      if (this.__closing) {
        clearTimeout(this.__closing);
        this.__closing = null;
        this.classList.remove('is-closing');
        return undefined;
      }
      return nativeShow.call(this);
    };
    // Esc goes through the same exit.
    document.addEventListener('cancel', (event) => {
      const d = event.target;
      if (d instanceof HTMLDialogElement && d.hasAttribute('data-motion') && !reduce()) {
        event.preventDefault();
        d.close();
      }
    }, true);
  }

  /* ---------- Accordions ---------- */
  document.addEventListener('click', (event) => {
    const summary = event.target.closest('summary');
    if (!summary || reduce()) return;
    const details = summary.parentElement;
    if (!(details instanceof HTMLDetailsElement) || details.hasAttribute('data-no-motion')) return;
    event.preventDefault();
    if (details.__anim) details.__anim.cancel();
    const start = details.offsetHeight;
    const closing = details.open;
    if (!closing) details.open = true;
    const end = closing ? summary.offsetHeight : details.scrollHeight;
    details.style.overflow = 'hidden';
    const body = [...details.children].filter((c) => c !== summary);
    details.__anim = details.animate({ height: [`${start}px`, `${end}px`] }, { duration: 320, easing: EASE });
    body.forEach((el) => el.animate({ opacity: closing ? [1, 0] : [0, 1] }, { duration: closing ? 200 : 320, easing: EASE }));
    details.__anim.onfinish = () => {
      details.__anim = null;
      details.style.overflow = '';
      if (closing) details.open = false;
    };
    details.__anim.oncancel = () => { details.style.overflow = ''; };
  });

  /* ---------- Things that appear ---------- */
  const REVEAL = '.pp-low-stock, [data-error], [data-sheet-error], [data-restock], [data-restock-done], [data-newsletter-done], .pp-klarna, .cart-drawer__error, [data-reveal]';
  const reveal = (el) => {
    if (reduce() || !el.matches(REVEAL)) return;
    el.animate({ opacity: [0, 1], transform: ['translateY(6px)', 'none'] }, { duration: 320, easing: EASE });
  };
  new MutationObserver((records) => {
    records.forEach((r) => {
      if (r.type === 'attributes' && r.attributeName === 'hidden' && r.oldValue !== null && !r.target.hidden) reveal(r.target);
    });
  }).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ['hidden'], attributeOldValue: true });

  /* ---------- Images ---------- */
  const fadeImg = (img) => {
    if (img.__motion || img.complete || reduce()) return;
    img.__motion = true;
    img.classList.add('m-img');
    const done = () => img.classList.add('is-loaded');
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', done, { once: true });
  };
  const IMG = 'img[loading="lazy"]';
  const scan = (root) => {
    if (root.matches && root.matches(IMG)) fadeImg(root);
    if (root.querySelectorAll) root.querySelectorAll(IMG).forEach(fadeImg);
  };
  scan(document);
  new MutationObserver((records) => records.forEach((r) => r.addedNodes.forEach((n) => n.nodeType === 1 && scan(n))))
    .observe(document.documentElement, { childList: true, subtree: true });
})();
